<template>
    <div class="h-screen flex overflow-hidden bg-charcoal-800">
        <!-- Off-canvas menu for mobile -->
        <div v-if="isMobileSidebarVisible">
            <div class="fixed inset-0 flex z-99999">
                <div class="fixed inset-0">
                    <div class="absolute inset-0 bg-black opacity-75"></div>
                </div>
                <div class="relative flex-1 flex flex-col max-w-64 w-full pb-4 bg-charcoal-800">
                    <div class="flex items-center h-16 flex-shrink-0 px-4 bg-charcoal-800 text-white uppercase">
                        Custodian Community
                    </div>
                    <div class="absolute top-0 right-0 -mr-14 mt-1 p-1">
                        <button class="flex items-center justify-center h-12 w-12" aria-label="Close sidebar" @click="toggleMobileSidebar">
                            <svg class="h-6 w-6 text-white" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                    <div class="h-0 flex-1 flex flex-col overflow-visible border-t border-charcoal-500">
                        <sidebar></sidebar>
                    </div>
                </div>
            </div>
        </div>

        <!-- Static sidebar for desktop -->
        <div class="hidden lg:flex lg:flex-shrink-0 w-60">
            <div class="flex flex-col flex-grow">
                <div class="flex flex-col items-start h-16 flex-shrink-0 px-4 bg-charcoal-900 text-white" :style="`background-color: ${backgroundColour};`">
                    <span class="uppercase pt-3">Custodian Community</span>
                    <span class="text-xs">{{ appName }}</span>
                </div>
                <div class="h-0 flex-1 flex flex-col overflow-visible">
                    <sidebar></sidebar>
                </div>
            </div>
        </div>
        <div class="flex flex-col w-0 flex-1 overflow-hidden">
            <div class="relative z-1 flex h-16 bg-charcoal-900 shadow items-center" :style="`background-color: ${backgroundColour};`">
                <button class="pl-4 text-gray-500 block lg:hidden" aria-label="Open sidebar" @click="toggleMobileSidebar">
                    <svg class="h-6 w-6" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h7" />
                    </svg>
                </button>
                <div class="flex-1 px-4 flex justify-between">
                    <div class="flex">
                        <div class="flex items-center h-16 flex-shrink-0 px-4 bg-charcoal-900 lg:hidden">
                            <img class="h-8 w-auto" src="@/assets/img/tap-logo.png" alt="TAP" />
                        </div>
                    </div>
                    <div class="flex justify-end items-center flex-grow">
                        <input-select class="w-64"
                            ref="selectedApplication"
                            placeholder="Open Application"
                            :options="[
                                {label: 'YNPT', value: 'https://admin.ynpt.app' },
                                {label: 'Palyku', value: 'https://admin.palyku.app' },
                                {label: 'TMWTJ', value: 'https://admin.tmwtj.app' },
                                {label: 'KPAC', value: 'https://admin.kpac.app' },
                                {label: 'Darlot', value: 'https://admin.darlot.app' },
                                {label: 'WT Trust', value: 'https://admin.wttrust.app' },
                            ]"
                            @input="openApplication"
                        />
                    </div>
                    <div class="ml-4 flex items-center md:ml-6">
                        <!-- Create dropdown -->
                        <div class="relative" v-click-outside="closeCreateMenu">
                            <div>
                                <button class="hover:bg-charcoal-600 text-white w-16 h-20 border-l border-r border-charcoal-400 mr-4 rounded-lg" @click="createMenuOpen = !createMenuOpen">
                                    <svg aria-hidden="true" focusable="false" data-prefix="far" data-icon="plus" class="svg-inline--fa fa-plus h-6 w-6" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path fill="currentColor" d="M368 224H224V80c0-8.84-7.16-16-16-16h-32c-8.84 0-16 7.16-16 16v144H16c-8.84 0-16 7.16-16 16v32c0 8.84 7.16 16 16 16h144v144c0 8.84 7.16 16 16 16h32c8.84 0 16-7.16 16-16V288h144c8.84 0 16-7.16 16-16v-32c0-8.84-7.16-16-16-16z"></path></svg>
                                </button>
                            </div>
                            <div class="origin-top-right absolute right-0 mr-4 w-40 shadow-lg z-10" :class="{ 'hidden': !createMenuOpen }">
                                <div class="py-1 bg-white shadow-xs" role="menu" aria-orientation="vertical" aria-labelledby="user-menu">
                                    <a href="javascript:void(0)"
                                        @click="handleCreateClick('/submissions/create')"
                                        class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150">
                                        Application
                                    </a>
                                    <a href="javascript:void(0)"
                                        @click="handleCreateClick('/members/create')"
                                        class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150">
                                        Member
                                    </a>
                                    <a href="javascript:void(0)"
                                        @click="handleCreateClick('/configuration/suppliers/create')"
                                        class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150">
                                        Supplier
                                    </a>
                                </div>
                            </div>
                        </div>

                        <!-- Profile dropdown -->
                        <div class="relative" v-click-outside="closeUserMenu">
                            <div>
                                <button class="max-w-xs flex items-center text-sm rounded-full focus:outline-none " id="user-menu" aria-label="User menu" aria-haspopup="true"  @click="userMenuOpen = !userMenuOpen">
                                    <span class="ml-4 text-gray-200 text-sm font-light">{{ fullName }}</span>
                                    <font-awesome-icon class="ml-2 text-white mt-px" :icon="['fas', 'chevron-down']" />
                                </button>
                            </div>
                            <div class="origin-top-right absolute right-0 mt-2 w-48 shadow-lg z-10" :class="{ 'hidden': !userMenuOpen }">
                                <div class="py-1 bg-white shadow-xs" role="menu" aria-orientation="vertical" aria-labelledby="user-menu">
                                    <a href="javascript:void(0);"
                                        class="block px-4 py-2 text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150"
                                        role="menuitem"
                                        @click="openMyobOAuth()">
                                        Connect to MYOB
                                    </a>
                                    <a href="javascript:void(0);"
                                        class="block px-4 py-2 text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150"
                                        role="menuitem"
                                        @click="openXeroOAuth()">
                                        Connect to Xero
                                    </a>
                                    <a href="javascript:void(0);"
                                        class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150"
                                        role="menuitem"
                                        @click="clearCache()">
                                        Clear Cache
                                    </a>
                                    <a href="javascript:void(0);"
                                        class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150"
                                        role="menuitem"
                                        @click="logout()">
                                        Sign out
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <main class="flex flex-grow relative z-0 overflow-y-auto hide-scrollbar focus:outline-none bg-white" tabindex="0" id="main_container">
                <vue-progress-bar></vue-progress-bar>
                <transition name="router-anim" enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
                    <router-view></router-view>
                </transition>

<!--                <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">-->
<!--                    <h1 class="text-2xl font-semibold text-gray-900">Dashboard</h1>-->
<!--                </div>-->
<!--                <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">-->
<!--                    <div class="py-4">-->
<!--                        <div class="border-4 border-dashed border-gray-200 rounded-lg h-96"></div>-->
<!--                    </div>-->
<!--                </div>-->
            </main>
        </div>
        <div class="bg-black opacity-75 z-99999 fixed top-0 bottom-0 left-0 right-0" v-show="showBackdrop">
            <div class="sk-circle">
                <div class="sk-circle1 sk-child"></div>
                <div class="sk-circle2 sk-child"></div>
                <div class="sk-circle3 sk-child"></div>
                <div class="sk-circle4 sk-child"></div>
                <div class="sk-circle5 sk-child"></div>
                <div class="sk-circle6 sk-child"></div>
                <div class="sk-circle7 sk-child"></div>
                <div class="sk-circle8 sk-child"></div>
                <div class="sk-circle9 sk-child"></div>
                <div class="sk-circle10 sk-child"></div>
                <div class="sk-circle11 sk-child"></div>
                <div class="sk-circle12 sk-child"></div>
            </div>
        </div>
        <notifications group="main" position="bottom right" />
    </div>
</template>

<script>
    import ClickOutside from 'vue-click-outside'
    import Sidebar from './Sidebar.vue';

    export default {
        name: "Main",
        components: {
            Sidebar
        },
        data: () => {
            return {
                userMenuOpen: false,
                createMenuOpen: false,
                selectedApplication: null
            }
        },
        mounted() {
            // window.addEventListener("keypress", function(e) {
            //     if (e.key === '/'){
            //         e.preventDefault();
            //         document.getElementById('search').focus();
            //     }
            // });
        },
        methods: {
            handleCreateClick: function(path){
                this.createMenuOpen = false;
                if (this.$router.currentRoute.path !== path){
                    this.$router.push(path);
                }
            },
            closeUserMenu: function(){
                this.userMenuOpen = false;
            },
            closeCreateMenu: function(){
                this.createMenuOpen = false;
            },
            openGoogleOAuth: function(){
                window.location.href = 'https://accounts.google.com/o/oauth2/v2/auth?' +
                    'access_type=offline&' +
                    'include_granted_scopes=true&' +
                    'response_type=code&' +
                    'redirect_uri='+ process.env.VUE_APP_GOOGLE_REDIRECT +'&' +
                    'client_id=494047875272-a2a3sd7792kjqgbdn7icdj3k4hokiggc.apps.googleusercontent.com&' +
                    'scope=https://mail.google.com/ https://www.googleapis.com/auth/gmail.compose https://www.googleapis.com/auth/gmail.insert https://www.googleapis.com/auth/gmail.modify https://www.googleapis.com/auth/gmail.send';
            },
            openMicrosoftOAuth: function(){
                window.location.href = 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize?' +
                    'client_id=f6b094e3-59c2-4134-9f24-5762cf194d7e' +
                    '&response_type=code' +
                    '&redirect_uri='+ process.env.VUE_APP_MICROSOFT_REDIRECT +
                    '&response_mode=query' +
                    '&access_type=offline' +
                    '&scope=https://graph.microsoft.com/offline_access https://graph.microsoft.com/User.Read https://graph.microsoft.com/Mail.ReadWrite https://graph.microsoft.com/Mail.Send https://graph.microsoft.com/Mail.ReadWrite.Shared https://graph.microsoft.com/Mail.Send.Shared' +
                    '&prompt=consent';
            },
            openXeroOAuth: function(){
                window.location.href = 'https://login.xero.com/identity/connect/authorize?' +
                    'client_id=07B0E56574AE43D1AFA2A7ED710EF596' +
                    '&response_type=code' +
                    '&redirect_uri='+ process.env.VUE_APP_XERO_REDIRECT +
                    '&scope=openid email profile offline_access accounting.contacts accounting.settings accounting.transactions accounting.attachments';
            },
            openMyobOAuth: function(){
                window.location.href = 'https://secure.myob.com/oauth2/account/authorize?' +
                    'client_id=4a12c3dc-6bfe-44be-82dc-5b9ff6de6c51' +
                    '&redirect_uri=' + process.env.VUE_APP_MYOB_REDIRECT +
                    '&response_type=code' +
                    '&scope=CompanyFile';
            },
            clearCache(){
                this.$store.commit('CLEAR_CACHE');
                this.$router.go();
            },
            toggleMobileSidebar(){
                this.$store.commit('TOGGLE_MOBILE_SIDEBAR');
            },
            logout(){
                this.$store.commit('CLEAR_CACHE');
                this.$store.commit('CLEAR_SESSION');
                this.$router.push('/login');
            },
            openApplication(payload){
                window.open(payload, '_blank');
                this.$refs.selectedApplication.clearSelect()
            }
        },
        directives: {
            ClickOutside
        },
        computed: {
            fullName: function () {
                return this.$store.getters.fullName;
            },
            modalOpen: function () {
                return this.$store.getters.isModalOpen;
            },
            showBackdrop: function () {
                return this.$store.getters.isBackdropVisible;
            },
            isMobileSidebarVisible: function () {
                return this.$store.getters.isMobileSidebarVisible;
            },
            appName: function () {
              return process.env.VUE_APP_INSTANCE_NAME;
            },
            backgroundColour: function () {
              return process.env.VUE_APP_HEADER_COLOUR;
            }
        }
    }
</script>

<style scoped>

</style>
